import { useCallback } from "react";

import { combineDateAndTime } from "@/common/utils/time-utils";
import {
	makeGeolocationPositionSerializable,
	useGetGpsLocation,
} from "./useGetGpsLocation";
import { onlineManager } from "@tanstack/react-query";
import { useOs } from "@mantine/hooks";
import { useLogger } from "@/logging/client/LoggerContext";

export type CheckInOutData = {
	scheduleId: number;
	checkIn?: string;
	checkOut?: string;
	checkInLatitude?: number;
	checkInLongitude?: number;
	checkOutLatitude?: number;
	checkOutLongitude?: number;
	checkInType?: 2;
	checkOutType?: 2;
	checkInMetadata?: any;
	checkOutMetadata?: any;
};

export function useGetCheckInOutData() {
	const { getGpsLocation } = useGetGpsLocation();
	const logger = useLogger();
	const os = useOs();
	const handleCheckInOut = useCallback(
		async ({
			shift,
			checkInTime,
			checkOutTime,
			checkInDate,
			checkOutDate,
			preemptiveGpsPosition,
		}: {
			shift: {
				id: number;
				start_timestamp: string;
				end_timestamp: string;
				evv_consent_granted: boolean;
			};
			checkInTime?: string;
			checkOutTime?: string;
			checkInDate?: string;
			checkOutDate?: string;
			preemptiveGpsPosition?: GeolocationPosition | null;
		}) => {
			let position: GeolocationPosition | null = null;
			let serializedPosition = null;
			let gpsError = null;

			if (shift.evv_consent_granted) {
				try {
					position = preemptiveGpsPosition
						? preemptiveGpsPosition
						: await getGpsLocation();
					serializedPosition = makeGeolocationPositionSerializable(position);
				} catch (err: any) {
					logger.error(
						`Check in/out GPS error: ${typeof err === "object" && err?.message ? err?.message : err}`,
					);
					gpsError = {
						message:
							typeof err === "string" ? err : err?.message || "Unknown error",
						code: typeof err === "string" ? null : err?.code || null,
						nativeCode:
							typeof err?.nativeCode === "string" ? err.nativeCode : null,
					};
				}
			} else {
				logger.warn("Check in/out GPS error: EVV consent not granted");
				gpsError = {
					message: "EVV consent not granted",
					code: null,
				};
			}

			const metadata = {
				gpsData: {
					...(serializedPosition || {}),
					captureMethod: "getCurrentPosition",
				},
				gpsError,
				userAgent: navigator.userAgent,
				appVersion: import.meta.env.VITE_APP_VERSION || "unknown",
				mobileApp: import.meta.env.VITE_CAPACITOR_BUILD === "true",
				os: os,
				offline: !onlineManager.isOnline(),
				timestamp: new Date().toISOString(),
			};

			const mutationData: CheckInOutData = {
				scheduleId: shift.id,
			};

			if (checkInTime) {
				if (!checkInDate) {
					throw new Error(
						"checkInDate is required when checkInTime is provided",
					);
				}
				const proposedDate = combineDateAndTime(checkInDate, checkInTime);
				mutationData.checkIn = proposedDate.toISOString();
				mutationData.checkInType = 2;
				mutationData.checkInMetadata = metadata;
				mutationData.checkInLatitude = position?.coords?.latitude;
				mutationData.checkInLongitude = position?.coords?.longitude;
			}

			if (checkOutTime) {
				if (!checkOutDate) {
					throw new Error(
						"checkOutDate is required when checkOutTime is provided",
					);
				}
				const proposedDate = combineDateAndTime(checkOutDate, checkOutTime);
				mutationData.checkOut = proposedDate.toISOString();
				mutationData.checkOutType = 2;
				mutationData.checkOutMetadata = metadata;
				mutationData.checkOutLatitude = position?.coords?.latitude;
				mutationData.checkOutLongitude = position?.coords?.longitude;
			}

			return mutationData;
		},
		[getGpsLocation, os],
	);

	return handleCheckInOut;
}
